import { useContext, useEffect, useRef } from 'react';

import { SocketContext } from '@frontend/contexts';

export function useSocket() {
  const ctx = useContext(SocketContext);
  if (!ctx) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return ctx;
}

export function useSocketChannel(channel: string, handler: (data: unknown) => void) {
  const { on, off, connected } = useSocket();

  useEffect(() => {
    if (!connected) return;

    on(channel, handler);

    return () => {
      off(channel, handler);
    };
  }, [channel, connected, handler, on, off]);
}

/**
 * Runs `onReconnect` whenever the socket reconnects after a prior connection
 * drop — i.e. on a `connected` false→true transition that is NOT the first
 * connect.
 *
 * Root-namespace socket events are fire-once with no replay, and the streaming
 * fallback poll only runs while disconnected. So any delta emitted during a
 * drop / re-subscribe window is lost. Use this to refetch (backfill) the
 * affected queries on reconnect, instead of forcing the user to refresh.
 *
 * The first connect is intentionally skipped: components already fetch their
 * initial data on mount (e.g. via React Query `enabled`), so backfill is only
 * needed for reconnections.
 */
export function useSocketReconnect(onReconnect: () => void) {
  const { connected } = useSocket();
  const hasConnectedBefore = useRef(false);

  // Keep the latest callback without re-running the effect (callers commonly
  // pass an inline closure that changes identity every render).
  const callbackRef = useRef(onReconnect);
  callbackRef.current = onReconnect;

  useEffect(() => {
    if (!connected) return;

    if (hasConnectedBefore.current) {
      callbackRef.current();
    } else {
      hasConnectedBefore.current = true;
    }
  }, [connected]);
}
